import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Spin } from "antd";
import moment from "moment";
import RadialBarChart from "../../../components/charts/RadialBarChart.jsx";
import { GetClientOrganizerApi } from "../../../api/request/clients/clients";
import YearPicker from "../../../components/common/YearPicker.jsx";

const Organizer = ({ id, tab }) => {
  const userDetails = useSelector((state) => state.user.userDetails);
  const [isLoading, setIsLoading] = useState(false);
  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--primary")
    ?.trim();
  const [activeOrgType, setActiveOrgType] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [organizers, setOrganizers] = useState([]);
  const navigate = useNavigate();

  const getOrganizer = async () => {
    if (!tab === "5") {
      return;
    }
    try {
      setIsLoading(true);
      let params = {
        is_archive: activeOrgType,
        client_id: id,
        ...(selectedYear ? { year: selectedYear } : {}),
      };
      const { data } = await GetClientOrganizerApi(params);
      if (data?.status) {
        setOrganizers(data?.data);
      }
      setIsLoading(false);
    } catch (error) {
      setOrganizers([]);
      setIsLoading(false);
    }
  };

  const tasks = [
    { id: 1, name: "verify_doc", label: "Verify Doc" },
    { id: 2, name: "prepare_tax", label: "Prepare Tax" },
    { id: 3, name: "review_tax", label: "Review Tax" },
    { id: 4, name: "follow_up", label: "Follow Up" },
    { id: 5, name: "e_file", label: "E File" },
  ];

  const getNextTask = (id) => {
    return tasks[tasks.findIndex((task) => task.id === id) + 1] || null;
  };

  const handleClick = (organizer) => {
    if (
      organizer?.status?.toLowerCase() === "finished" ||
      organizer?.completion_percentage === 100 ||
      Number(organizer?.completed_tasks) >= 5
    ) {
      navigate(`/client/organizer/${organizer?.id}`);
      return;
    }

    const nextTask = getNextTask(Number(organizer?.completed_tasks));
    if (!nextTask) {
      navigate(`/client/organizer/${organizer?.id}`);
      return;
    }

    const isAuthorized = userDetails?.permissions?.includes(nextTask?.label);

    if (isAuthorized) {
      navigate(`/${nextTask?.name}/${organizer?.id}`);
    } else {
      navigate(`/client/organizer/${organizer?.id}`);
    }
  };

  useEffect(() => {
    getOrganizer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, activeOrgType, selectedYear]);

  return (
    <div>
      <div className="bg-secondaryLight rounded-t-md flex justify-between items-center px-4 py-3">
        <div className="flex gap-6 uppercase font-semibold">
          <p
            className={`${activeOrgType ? "text-gray-500" : "text-primary border-b-2 border-primary"
              } cursor-pointer duration-300 pb-1`}
            onClick={() => setActiveOrgType(false)}
          >
            Active Organizer
          </p>
          <p
            className={`${activeOrgType ? "text-primary border-b-2 border-primary" : "text-gray-500"
              } cursor-pointer duration-300 pb-1`}
            onClick={() => setActiveOrgType(true)}
          >
            Archive
          </p>
        </div>

        <div className="flex items-center gap-2">
          <YearPicker
            value={selectedYear}
            onChange={(year) => setSelectedYear(year)}
            placeholder="Select Year"
          />
          {selectedYear && (
            <button
              onClick={() => setSelectedYear(null)}
              className="text-xs text-primary hover:underline font-semibold"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div>
        {isLoading ? (
          <div className="flex justify-center flex-col my-[10%]">
            <Spin size="large" />
            <p className="primary text-center mt-2">Loading...</p>
          </div>
        ) : (
          <div>
            {organizers?.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-5 p-4">
                {organizers?.map((item, index) => (
                  <div
                    className="border-2 rounded-2xl py-4 px-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleClick(item)}
                    key={index}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-lg capitalize">
                        {item?.organizer_type?.organizer_name}
                      </p>
                      <span className="bg-primaryLight text-primary text-xs font-bold px-2.5 py-1 rounded-full">
                        {moment(item?.created_at).format("YYYY")}
                      </span>
                    </div>
                    <div
                      key={index}
                      className="flex items-center justify-between cursor-pointer"
                    >
                      <div>
                        <p className="font-medium mt-3">
                          <span className="text-secondaryLight1">
                            Last Updated :{" "}
                          </span>
                          {moment(item?.updated_at).format("ll")}
                        </p>
                        {item?.is_submit ? (
                          <div className="flex items-center gap-2 mt-3">
                            <div className="size-3 rounded-full bg-primary"></div>
                            <p className="font-medium capitalize">
                              {item?.status}
                            </p>
                          </div>
                        ) : (
                          <button className="rounded-full bg-primary text-white text-sm font-semibold uppercase py-1 px-6 mt-3">
                            Pending
                          </button>
                        )}
                      </div>
                      <div>
                        <RadialBarChart
                          values={[item?.completion_percentage || 50]}
                          labels={["Completed"]}
                          colors={[primaryColor]}
                          value={true}
                          height={150}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="flex justify-center mt-40 text-secondaryLight1">
                No organizer found
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Organizer;
